import { useEffect, useState } from 'react';
import { searchOnEnter } from '../../components/searchOnEnter';
import { useHistory } from 'react-router-dom';
import Sidebar from '../../components/AdminDashboard/Sidebar';
import TopBar from '../../components/AdminDashboard/TopBar';
import MetaData from '../../layouts/MetaData';
import { RoleBadge } from '../../utils/roleLabels';
import { personnelApi, errMsg, AlertBadge } from '../../components/Personnel/personnelApi';
import CredentialAlertsPanel from '../../components/Personnel/CredentialAlertsPanel';
import AddPersonnelModal from '../../components/Personnel/AddPersonnelModal';
import { useSortableTable, SortableTh, nameFromString } from '../../components/SortableTable';

const PERSONNEL_SORT_COLUMNS = {
    name: { type: 'name', value: (p) => nameFromString(p?.name) },
    email: { type: 'string', value: (p) => p?.email },
    source: { type: 'string', value: (p) => p?.sourceType },
    role: { type: 'string', value: (p) => p?.roleLabel || p?.role },
    status: { type: 'boolean', value: (p) => p?.active },
};

// HR Personnel: every admin, doctor and nurse account in one directory (server-scoped by tenancy).
const Personnel = () => {
  const history = useHistory();
  const [filters, setFilters] = useState({ q: '', role: '', org: '', status: '', source: '' });
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const tableSort = useSortableTable(data?.items, { id: 'admin.personnel', columns: PERSONNEL_SORT_COLUMNS, defaultSort: { key: 'name', dir: 'asc' } });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [createOptions, setCreateOptions] = useState(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    personnelApi.createOptions().then(setCreateOptions).catch(() => setCreateOptions(null));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.q === q ? f : { ...f, q })), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    personnelApi
      .directory(params)
      .then((d) => alive && (setData(d), setError(null)))
      .catch((e) => alive && setError(errMsg(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [filters]);

  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });
  const orgName = Object.fromEntries((data?.organizations || []).map((o) => [o.id, o.name]));

  return (
    <>
      <MetaData title="HR Personnel" />
      <Sidebar />
      <section className="home-section">
        <TopBar />
        <CredentialAlertsPanel />
        <div className="shadow-lg p-3 mb-5 mr-4 ml-4 rounded-card">
          <div className="home-content">
            <div className="d-flex justify-content-between align-items-center">
              <h5 style={{ color: '#004aad' }}>
                <i className="fas fa-id-card"></i> HR Personnel{' '}
                {data && (
                  <small className="text-muted">
                    ({data.count} of {data.total}
                    {data.duplicateEmailCount ? `, ${data.duplicateEmailCount} with duplicate emails` : ''})
                  </small>
                )}
              </h5>
              <div className="d-flex align-items-center" style={{ gap: 8 }}>
                {data?.access === 'read' && <span className="badge bg-secondary">Read-only (your organizations)</span>}
                {createOptions?.canCreate && (
                  <button type="button" className="edit-pill" onClick={() => setAdding(true)}>
                    <i className="fas fa-user-plus"></i> Add Personnel
                  </button>
                )}
              </div>
            </div>

            <div className="row mt-2">
              <div className="col-md-4 mb-2">
                <input className="form-control" placeholder="Search name or email" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={searchOnEnter((v) => { setQ(v); setFilters((f) => (f.q === v ? f : { ...f, q: v })); })} />
              </div>
              <div className="col-md-2 mb-2">
                <select className="form-control" value={filters.role} onChange={set('role')}>
                  <option value="">All roles</option>
                  {(data?.roles || []).map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2 mb-2">
                <select className="form-control" value={filters.org} onChange={set('org')}>
                  <option value="">All organizations</option>
                  {(data?.organizations || []).map((o) => (
                    <option key={o.id} value={o.id}>{o.name}{o.status !== 'active' ? ' (inactive)' : ''}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-2 mb-2">
                <select className="form-control" value={filters.status} onChange={set('status')}>
                  <option value="">Any status</option>
                  <option value="active">Active</option>
                  <option value="blocked">Deactivated</option>
                </select>
              </div>
              <div className="col-md-2 mb-2">
                <select className="form-control" value={filters.source} onChange={set('source')}>
                  <option value="">All sources</option>
                  <option value="admin">Admin</option>
                  <option value="doctor">Doctor</option>
                  <option value="hr">Nurse</option>
                </select>
              </div>
            </div>

            {error && <div className="alert alert-danger">{error}</div>}
            {loading && !data && <p>Loading...</p>}
            {data && (
              <table className="styled-table" style={{ width: '100%' }}>
                <thead align="center">
                  <tr>
                    <SortableTh sortKey="name" sort={tableSort}>Name</SortableTh>
                    <SortableTh sortKey="email" sort={tableSort}>Email</SortableTh>
                    <SortableTh sortKey="source" sort={tableSort}>Source</SortableTh>
                    <SortableTh sortKey="role" sort={tableSort}>Role</SortableTh>
                    <th>Organizations</th>
                    <SortableTh sortKey="status" sort={tableSort}>Status</SortableTh>
                    <th>Credentials</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted">No personnel match these filters.</td>
                    </tr>
                  )}
                  {tableSort.rows.map((p) => (
                    <tr
                      key={`${p.kind}:${p.id}`}
                      onClick={() => history.push(`/personnel/${p.kind}/${p.id}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td><b>{p.name}</b></td>
                      <td>
                        {p.email}{' '}
                        {p.duplicateEmail && (
                          <span
                            title={`Same email on ${p.duplicates.map((d) => d.sourceType).join(', ')} account(s)`}
                            style={{ background: '#fef3c7', color: '#92400e', borderRadius: 5, padding: '1px 6px', fontSize: 12 }}
                          >
                            Duplicate email
                          </span>
                        )}
                      </td>
                      <td>{p.sourceType}</td>
                      <td><RoleBadge role={p.role} organizationIds={p.organizationIds} label={p.roleLabel} /></td>
                      <td style={{ fontSize: 13 }}>
                        {p.organizationIds.length ? p.organizationIds.map((id) => orgName[id] || id).join(', ') : <span className="text-muted">Global</span>}
                      </td>
                      <td>
                        {p.active ? (
                          <span style={{ color: '#15803d' }}>Active</span>
                        ) : (
                          <span style={{ color: '#b91c1c' }}>Deactivated</span>
                        )}
                      </td>
                      <td><AlertBadge level={p.credentialAlert} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>
      <AddPersonnelModal
        show={adding}
        onHide={() => setAdding(false)}
        options={createOptions}
        onCreated={(p) => {
          setAdding(false);
          history.push(`/personnel/${p.kind}/${p.id}`);
        }}
      />
    </>
  );
};

export default Personnel;
